import { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { adminAPI } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Search, Users, Mail, Download, TrendingUp, Loader2 } from "lucide-react";

function toCSV(rows) {
    if (!rows.length) return "";
    const cols = ["email", "source", "language", "touches", "created_at", "last_seen_at", "anon_id"];
    const escape = (v) => {
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    return [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))].join("\n");
}

export default function AdminLeads() {
    const { user, loading } = useApp();
    const [data, setData] = useState(null);
    const [stats, setStats] = useState(null);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const load = async (query = "") => {
        setBusy(true); setError(null);
        try {
            const [l, s] = await Promise.all([adminAPI.leads(query), adminAPI.stats()]);
            setData(l); setStats(s);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Error";
            setError(msg);
        } finally { setBusy(false); }
    };

    useEffect(() => { if (user) load(""); }, [user]);
    useEffect(() => { const t = setTimeout(() => user && load(q), 300); return () => clearTimeout(t); }, [q]);

    const downloadCSV = () => {
        if (!data?.leads?.length) { toast.error("Nothing to export"); return; }
        const blob = new Blob([toCSV(data.leads)], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `shorts-factory-leads-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click(); URL.revokeObjectURL(url);
    };

    const sources = useMemo(() => Object.entries(data?.by_source || {}).sort((a, b) => b[1] - a[1]), [data]);

    if (loading) return <div className="min-h-screen bg-[#080B10] flex items-center justify-center text-slate-500">···</div>;
    if (!user) return <Navigate to="/auth" replace />;

    return (
        <div className="min-h-screen bg-[#080B10]">
            <Navbar />
            <div data-testid="admin-leads-container" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <div className="overline">Admin</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase mt-1">Lead <span className="flame-text">Dashboard</span></h1>
                        <p className="text-sm text-slate-400 mt-2">Emails captured via the waitlist and founder deal. Warm them manually before your first ad drop.</p>
                    </div>
                    <button
                        data-testid="admin-leads-export"
                        onClick={downloadCSV}
                        disabled={!data?.leads?.length}
                        className="px-4 py-2.5 rounded border border-[#253046] hover:border-[#FF4D00] text-sm text-slate-100 flex items-center gap-2 kinetic disabled:opacity-50"
                    >
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                </div>

                {error && (
                    <div data-testid="admin-error" className="mt-6 p-4 rounded border border-red-500/40 bg-red-500/10 text-red-300 text-sm">
                        {error === "Admin access required" ? "Ce compte n'a pas les droits admin. Ajoute ton email à ADMIN_EMAILS dans backend/.env." : error}
                    </div>
                )}

                {!error && (
                    <>
                        {/* Stats */}
                        <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-2">
                            {[
                                { icon: Users,   label: "Users",      k: "users_total",       tint: "text-[#FF4D00]" },
                                { icon: Users,   label: "Paid users", k: "users_paid",        tint: "text-[#10B981]" },
                                { icon: TrendingUp,label:"Payments",  k: "payments_paid",     tint: "text-[#FFB800]" },
                                { icon: Users,   label:"Referrals",   k: "referrals_credited",tint: "text-[#0099FF]" },
                                { icon: Mail,    label:"Leads",       k: "leads_total",       tint: "text-[#FF4D00]" },
                            ].map((c) => {
                                const Icon = c.icon;
                                return (
                                    <div key={c.k} data-testid={`admin-stat-${c.k}`} className="bg-[#161D2A] border border-[#253046] rounded p-4">
                                        <Icon className={`w-4 h-4 ${c.tint}`} />
                                        <div className="font-display text-3xl font-black mt-2">{stats?.[c.k] ?? "—"}</div>
                                        <div className="text-[10px] font-mono-sf text-slate-500 uppercase tracking-widest mt-1">{c.label}</div>
                                    </div>
                                );
                            })}
                        </div>

                        {sources.length > 0 && (
                            <div className="mt-6 bg-[#161D2A] border border-[#253046] rounded p-4">
                                <div className="overline">Leads by source</div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {sources.map(([src, count]) => (
                                        <div key={src} className="px-3 py-1.5 rounded border border-[#253046] bg-[#0B111B] flex items-center gap-2">
                                            <span className="font-mono-sf text-[10px] text-slate-400 uppercase">{src}</span>
                                            <span className="font-display font-black text-white">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Search + table */}
                        <div className="mt-8 flex items-center gap-3">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <input
                                    data-testid="admin-leads-search"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search email…"
                                    className="w-full pl-9 pr-3 py-2.5 bg-[#0F141C] border border-[#253046] rounded text-sm focus:outline-none focus:border-[#FF4D00]"
                                />
                            </div>
                            {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
                            <div className="text-xs text-slate-500 font-mono-sf ml-auto">
                                {data?.returned ?? 0} / {data?.total ?? 0} results
                            </div>
                        </div>

                        <div className="mt-4 rounded border border-[#253046] bg-[#0B111B] overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-[#161D2A] border-b border-[#253046]">
                                    <tr className="text-left text-[10px] font-mono-sf text-slate-500 uppercase tracking-widest">
                                        <th className="py-3 px-4">Email</th>
                                        <th className="py-3 px-3">Source</th>
                                        <th className="py-3 px-3">Lang</th>
                                        <th className="py-3 px-3">Touches</th>
                                        <th className="py-3 px-3">Created</th>
                                        <th className="py-3 px-3">Last seen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.leads || []).map((l, i) => (
                                        <tr key={l.id || i} data-testid="admin-lead-row" className="border-t border-[#253046] hover:bg-[#161D2A]/60">
                                            <td className="py-2.5 px-4 text-slate-100">{l.email}</td>
                                            <td className="py-2.5 px-3">
                                                <span className="text-[10px] font-mono-sf uppercase px-2 py-0.5 rounded bg-[#0F141C] border border-[#253046] text-slate-300">{l.source}</span>
                                            </td>
                                            <td className="py-2.5 px-3 text-slate-400 font-mono-sf text-xs">{l.language || "—"}</td>
                                            <td className="py-2.5 px-3">
                                                <span className={`font-display font-black ${l.touches > 1 ? "text-[#FFB800]" : "text-slate-300"}`}>{l.touches || 1}</span>
                                            </td>
                                            <td className="py-2.5 px-3 text-slate-500 font-mono-sf text-xs">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                            <td className="py-2.5 px-3 text-slate-500 font-mono-sf text-xs">{(l.last_seen_at || "").slice(0, 16).replace("T", " ")}</td>
                                        </tr>
                                    ))}
                                    {!busy && (data?.leads?.length ?? 0) === 0 && (
                                        <tr><td colSpan={6} className="py-10 text-center text-slate-500 text-sm">No leads yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
